import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { FaIcon } from '../FaIcon';
import { filterFieldHint } from '../../utils/filterNotes';
import '../../sass/elements/_dimensions-filters.scss';

class DimensionsFiltersComponentClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isPopupOpen: false,
            searchText: '',
            selectedField: null,
            filterOperator: null,
            filterValues: [],
            tempFilterValue: '',
            tempFilterValue2: '',
            showOperatorMenu: false,
            showOptionsMenu: false,
        };
        this.inputRef = React.createRef();
        this.popupRef = React.createRef();
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleFieldSelect = this.handleFieldSelect.bind(this);
        this.handleOperatorSelect = this.handleOperatorSelect.bind(this);
        this.handleApplyFilter = this.handleApplyFilter.bind(this);
        this.handleRemoveFilter = this.handleRemoveFilter.bind(this);
        this.handleClearAll = this.handleClearAll.bind(this);
        this.getAvailableOperators = this.getAvailableOperators.bind(this);
        this.getFilterDisplayText = this.getFilterDisplayText.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside(event) {
        if (this.popupRef.current && !this.popupRef.current.contains(event.target) &&
            this.inputRef.current && !this.inputRef.current.contains(event.target)) {
            this.setState({ 
                isPopupOpen: false,
                selectedField: null,
                filterOperator: null,
                filterValues: [],
                tempFilterValue: '',
                tempFilterValue2: '',
                showOperatorMenu: false,
                showOptionsMenu: false,
            });
        }
    }

    handleSearchChange(event) {
        this.setState({ searchText: event.target.value });
    }

    /**
     * Verifica si el campo es BusinessUnitStatus
     * @param {string} fieldId - ID del campo
     * @returns {boolean}
     */
    isBusinessUnitStatusField(fieldId) {
        if (!fieldId) return false;
        const fieldIdLower = fieldId.toLowerCase();
        return fieldIdLower.includes('presupuesto_estado');
    }

    /**
     * Obtiene las opciones para el campo BusinessUnitStatus
     * @returns {Array<{value: string, text: string}>}
     */
    getBusinessUnitStatusOptions() {
        const { t } = this.props;
        return [
            { value: 'ACTIVE', text: t('data_explorer.business_unit_status.active', 'Activo') },
            { value: 'FINISHED', text: t('data_explorer.business_unit_status.finished', 'Finalizado') },
            { value: 'NOT_STARTED_YET', text: t('data_explorer.business_unit_status.not_started_yet', 'No iniciado') },
            { value: 'NOT_ACTIVE', text: t('data_explorer.business_unit_status.not_active', 'Inactivo') },
        ];
    }

    /**
     * Obtiene las opciones para un campo desde fieldsData.OPTIONS_BY_FIELD_ID
     * @param {string} fieldId - ID del campo
     * @returns {Array} - Array de opciones
     */
    getFieldOptions(fieldId) {
        const { fieldsData } = this.props;
        
        // Obtener opciones desde fieldsData.OPTIONS_BY_FIELD_ID
        const fieldOptions = fieldsData.OPTIONS_BY_FIELD_ID && fieldsData.OPTIONS_BY_FIELD_ID[fieldId];
        return fieldOptions || [];
    }

    /**
     * Traduce el texto de una opción si es BusinessUnitStatus
     * @param {string} fieldId - ID del campo
     * @param {string} optionValue - Valor de la opción
     * @returns {string} - Texto traducido o el valor original
     */
    translateOptionText(fieldId, optionValue) {
        // Si es BusinessUnitStatus, traducir el valor
        if (this.isBusinessUnitStatusField(fieldId)) {
            return this.translateBusinessUnitStatusValue(optionValue);
        }
        return optionValue;
    }

    handleFieldSelect(fieldId) {
        const { fieldsData } = this.props;
        if (!fieldsData || !fieldsData.FIELD_DATA_BY_ID || !fieldsData.FIELD_DATA_BY_ID[fieldId]) {
            return;
        }

        const field = fieldsData.FIELD_DATA_BY_ID[fieldId];
        const fieldOptions = this.getFieldOptions(fieldId);
        
        // Si tiene opciones predefinidas o es BusinessUnitStatus, mostrar menú de opciones
        if (fieldOptions && fieldOptions.length > 0) {
            this.setState({
                selectedField: fieldId,
                filterOperator: 'IN_LIST',
                filterValues: [],
                showOptionsMenu: true,
                showOperatorMenu: false,
            });
        } else {
            // Determinar operador por defecto según el tipo de dato
            const defaultOperator = this.getDefaultOperatorForDataType(field.data_type);
            this.setState({
                selectedField: fieldId,
                filterOperator: defaultOperator,
                filterValues: [],
                tempFilterValue: '',
                tempFilterValue2: '',
                showOperatorMenu: true,
                showOptionsMenu: false,
            });
        }
    }

    getDefaultOperatorForDataType(dataType) {
        const { DATA_TYPES } = this.props.fieldsData || {};
        if (!DATA_TYPES) return 'EQUALS';

        if (dataType === DATA_TYPES.TEXT) {
            return 'CONTAINS';
        } else if ([DATA_TYPES.DECIMAL, DATA_TYPES.INTEGER, DATA_TYPES.CURRENCY, DATA_TYPES.PERCENT].includes(dataType)) {
            return 'NUMERIC_GREATER_THAN';
        } else if (dataType === DATA_TYPES.BOOLEAN) {
            return 'EQUALS';
        } else if (dataType === DATA_TYPES.DATE) {
            return 'BETWEEN';
        }
        return 'EQUALS';
    }

    getAvailableOperators(fieldId) {
        const { fieldsData } = this.props;
        if (!fieldsData || !fieldsData.FIELD_DATA_BY_ID || !fieldsData.FIELD_DATA_BY_ID[fieldId]) {
            return [];
        }

        const field = fieldsData.FIELD_DATA_BY_ID[fieldId];
        const { DATA_TYPES } = fieldsData;
        const fieldOptions = this.getFieldOptions(fieldId);

        // Si tiene opciones predefinidas o es BusinessUnitStatus, solo permitir IN_LIST
        if (fieldOptions && fieldOptions.length > 0) {
            return [{ value: 'IN_LIST', label: this.props.t('filters.in_list', 'está en') }];
        }

        const operators = [];

        if (field.data_type === DATA_TYPES.TEXT) {
            operators.push(
                { value: 'CONTAINS', label: this.props.t('filters.contains', 'contiene') },
                { value: 'EQUALS', label: this.props.t('filters.equals', 'es igual a') },
                { value: 'REGEXP_PARTIAL_MATCH', label: this.props.t('filters.regexp_partial_match', 'coincide con regexp') },
                { value: 'REGEXP_EXACT_MATCH', label: this.props.t('filters.regexp_exact_match', 'coincide exactamente con regexp') },
                { value: 'IS_NULL', label: this.props.t('filters.is_null', 'es nulo') }
            );
        } else if ([DATA_TYPES.DECIMAL, DATA_TYPES.INTEGER, DATA_TYPES.CURRENCY, DATA_TYPES.PERCENT].includes(field.data_type)) {
            operators.push(
                { value: 'NUMERIC_GREATER_THAN', label: this.props.t('filters.greater_than', 'es mayor que') },
                { value: 'NUMERIC_GREATER_THAN_OR_EQUAL', label: this.props.t('filters.greater_than_or_equal', 'es mayor o igual que') },
                { value: 'NUMERIC_LESS_THAN', label: this.props.t('filters.less_than', 'es menor que') },
                { value: 'NUMERIC_LESS_THAN_OR_EQUAL', label: this.props.t('filters.less_than_or_equal', 'es menor o igual que') },
                { value: 'EQUALS', label: this.props.t('filters.equals', 'es igual a') },
                { value: 'BETWEEN', label: this.props.t('filters.between', 'está entre') },
                { value: 'IS_NULL', label: this.props.t('filters.is_null', 'es nulo') }
            );
        } else if (field.data_type === DATA_TYPES.BOOLEAN) {
            operators.push(
                { value: 'EQUALS', label: this.props.t('filters.equals', 'es igual a') },
                { value: 'IS_NULL', label: this.props.t('filters.is_null', 'es nulo') }
            );
        } else if (field.data_type === DATA_TYPES.DATE) {
            operators.push(
                { value: 'BETWEEN', label: this.props.t('filters.between', 'está entre') },
                { value: 'NUMERIC_GREATER_THAN', label: this.props.t('filters.greater_than', 'es mayor que') },
                { value: 'NUMERIC_LESS_THAN', label: this.props.t('filters.less_than', 'es menor que') },
                { value: 'IS_NULL', label: this.props.t('filters.is_null', 'es nulo') }
            );
        }

        return operators;
    }

    handleOperatorSelect(operator) {
        this.setState({ 
            filterOperator: operator,
            showOperatorMenu: false,
            filterValues: [],
            tempFilterValue: '',
            tempFilterValue2: '',
        });
    }

    handleOptionToggle(optionValue) {
        this.setState(prevState => {
            const currentValues = prevState.filterValues || [];
            if (currentValues.includes(optionValue)) {
                return { filterValues: currentValues.filter(v => v !== optionValue) };
            } else {
                return { filterValues: [...currentValues, optionValue] };
            }
        });
    }

    handleApplyFilter() {
        const { selectedField, filterOperator, filterValues, tempFilterValue, tempFilterValue2 } = this.state;
        const { filters, onFiltersChange } = this.props;

        if (!selectedField || !filterOperator) return;

        let values = [];
        
        if (filterOperator === 'BETWEEN') {
            if (!tempFilterValue || !tempFilterValue2) return;
            values = [tempFilterValue, tempFilterValue2];
        } else if (filterOperator === 'IS_NULL') {
            values = [];
        } else if (filterValues.length > 0) {
            values = filterValues;
        } else if (tempFilterValue) {
            values = [tempFilterValue];
        } else {
            return; // No hay valores para aplicar
        }

        // Crear el nuevo filtro
        const newFilter = {
            fieldName: selectedField,
            operator: filterOperator,
            type: 'INCLUDE',
            values: values
        };

        // Obtener filtros actuales
        const currentFilters = filters || [];
        
        // Agregar el nuevo filtro como un nuevo grupo (AND)
        const updatedFilters = [...currentFilters, [newFilter]];

        // Notificar al componente padre
        if (onFiltersChange) {
            onFiltersChange(updatedFilters);
        }

        // Resetear el estado
        this.setState({
            selectedField: null,
            filterOperator: null,
            filterValues: [],
            tempFilterValue: '',
            tempFilterValue2: '',
            showOperatorMenu: false,
            showOptionsMenu: false,
            isPopupOpen: false,
            searchText: '',
        });
    }

    handleRemoveFilter(filterGroupIndex, filterIndex) {
        const { filters, onFiltersChange } = this.props;
        if (!filters || !onFiltersChange) return;

        const updatedFilters = [...filters];
        const group = updatedFilters[filterGroupIndex];
        
        if (group.length === 1) {
            // Si solo hay un filtro en el grupo, eliminar el grupo completo
            updatedFilters.splice(filterGroupIndex, 1);
        } else {
            // Eliminar el filtro específico del grupo
            updatedFilters[filterGroupIndex] = group.filter((_, idx) => idx !== filterIndex);
        }

        onFiltersChange(updatedFilters.length > 0 ? updatedFilters : []);
    }

    handleClearAll() {
        const { onFiltersChange } = this.props;
        if (onFiltersChange) {
            onFiltersChange([]);
        }
    }

    /**
     * Traduce un valor del enum de BusinessUnitStatus
     * @param {string} enumValue - Valor del enum
     * @returns {string} - Texto traducido
     */
    translateBusinessUnitStatusValue(enumValue) {
        const { t } = this.props;
        if (!enumValue || typeof enumValue !== 'string') {
            return enumValue;
        }
        
        const statusMap = {
            'ACTIVE': t('data_explorer.business_unit_status.active', 'Activo'),
            'FINISHED': t('data_explorer.business_unit_status.finished', 'Finalizado'),
            'NOT_STARTED_YET': t('data_explorer.business_unit_status.not_started_yet', 'No iniciado'),
            'NOT_ACTIVE': t('data_explorer.business_unit_status.not_active', 'Inactivo'),
        };
        
        return statusMap[enumValue.toUpperCase()] || enumValue;
    }

    getFilterDisplayText(filter) {
        const { fieldsData, t } = this.props;
        if (!fieldsData || !fieldsData.FIELD_DATA_BY_ID) return '';

        const field = fieldsData.FIELD_DATA_BY_ID[filter.fieldName];
        const fieldName = field ? field.name : filter.fieldName;

        const operatorLabels = {
            'EQUALS': t('filters.equals', 'es igual a'),
            'CONTAINS': t('filters.contains', 'contiene'),
            'REGEXP_PARTIAL_MATCH': t('filters.regexp_partial_match', 'coincide con regexp'),
            'REGEXP_EXACT_MATCH': t('filters.regexp_exact_match', 'coincide exactamente con regexp'),
            'IN_LIST': t('filters.in_list', 'está en'),
            'IS_NULL': t('filters.is_null', 'es nulo'),
            'BETWEEN': t('filters.between', 'está entre'),
            'NUMERIC_GREATER_THAN': '>',
            'NUMERIC_GREATER_THAN_OR_EQUAL': '>=',
            'NUMERIC_LESS_THAN': '<',
            'NUMERIC_LESS_THAN_OR_EQUAL': '<=',
        };

        const operatorLabel = operatorLabels[filter.operator] || filter.operator;

        if (filter.operator === 'IS_NULL') {
            return `${fieldName} ${operatorLabel}`;
        }

        if (filter.operator === 'BETWEEN' && filter.values.length === 2) {
            return `${fieldName} ${operatorLabel} ${filter.values[0]} y ${filter.values[1]}`;
        }

        // Si es el campo BusinessUnitStatus, traducir los valores
        const isBusinessUnitStatus = this.isBusinessUnitStatusField(filter.fieldName);
        const valuesText = filter.values.length > 0 
            ? filter.values.map(value => isBusinessUnitStatus ? this.translateBusinessUnitStatusValue(value) : value).join(', ')
            : '';

        return `${fieldName} ${operatorLabel} ${valuesText}`;
    }

    getFilteredFields() {
        const { fieldsData, sourceType } = this.props;
        const { searchText } = this.state;
        if (!fieldsData || !fieldsData.FIELDS_BY_DATA_SOURCE_TYPE || !fieldsData.FIELD_DATA_BY_ID || !fieldsData.FIELDS_GROUP_DATA) {
            return {};
        }

        const { FIELDS_BY_DATA_SOURCE_TYPE, FIELD_DATA_BY_ID, FIELD_SEGMENTS, FIELDS_GROUP_DATA } = fieldsData;
        const availableFieldIds = FIELDS_BY_DATA_SOURCE_TYPE[sourceType] || [];

        const fieldsByGroup = {};
        Object.keys(FIELDS_GROUP_DATA).forEach(groupId => {
            fieldsByGroup[groupId] = [];
        });

        availableFieldIds.forEach(id => {
            const field = FIELD_DATA_BY_ID[id] ? { ...FIELD_DATA_BY_ID[id], id } : null;
            // Permitir filtrar tanto por métricas como por dimensiones
            if (field && (field.segment === FIELD_SEGMENTS.METRICS || field.segment === FIELD_SEGMENTS.DIMENSIONS) && field.group) {
                // Filtrar por texto de búsqueda
                const matchesSearch = !searchText || 
                    field.name.toLowerCase().includes(searchText.toLowerCase()) ||
                    id.toLowerCase().includes(searchText.toLowerCase());
                
                if (matchesSearch && fieldsByGroup[field.group]) {
                    fieldsByGroup[field.group].push(field);
                }
            }
        });

        return fieldsByGroup;
    }

    render() {
        const { t, filters = [], fieldsData } = this.props;
        const { 
            isPopupOpen, 
            searchText, 
            selectedField, 
            filterOperator, 
            filterValues, 
            tempFilterValue, 
            tempFilterValue2,
            showOperatorMenu,
            showOptionsMenu
        } = this.state;

        if (!fieldsData) return null;

        const fieldsByGroup = this.getFilteredFields();
        const appliedFilters = filters.flat();
        // Qué alcanza el filtro elegido (`copy.md` §4 de la tanda «Filtros que se respetan»): ayuda
        // secundaria debajo del nombre del campo, nunca adentro del chip. `null` para los campos
        // que no la llevan, y entonces el editor es el de siempre.
        const selectedFieldHint = selectedField ? filterFieldHint(selectedField, fieldsData, t) : null;

        return (
            <div className="dimensions-filters">
                <div
                    ref={this.inputRef}
                    className="dimensions-filters__input-container"
                    onClick={() => {
                        if (this.inputRef.current && this.inputRef.current.querySelector('input')) {
                            this.inputRef.current.querySelector('input').focus();
                        }
                        this.setState({ isPopupOpen: true });
                    }}
                >
                    {appliedFilters.map((filter, filterIndex) => {
                        // Encontrar el índice del grupo
                        let groupIndex = 0;
                        let localIndex = 0;
                        let found = false;
                        for (let i = 0; i < filters.length; i++) {
                            if (filters[i].includes(filter)) {
                                groupIndex = i;
                                localIndex = filters[i].indexOf(filter);
                                found = true;
                                break;
                            }
                        }
                        if (!found) return null;

                        return (
                            <span
                                key={`${groupIndex}-${localIndex}`}
                                className="dimensions-filters__filter-tag"
                            >
                                <span>{this.getFilterDisplayText(filter)}</span>
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleRemoveFilter(groupIndex, localIndex);
                                    }}
                                    className="dimensions-filters__filter-tag-remove"
                                >
                                    ×
                                </span>
                            </span>
                        );
                    })}
                    <input
                        type="text"
                        value={searchText}
                        onChange={this.handleSearchChange}
                        onFocus={() => this.setState({ isPopupOpen: true })}
                        placeholder={appliedFilters.length === 0 ? t('filters.search_placeholder', 'Busque para filtrar por nombre, Id o métrica') : ''}
                        className={`dimensions-filters__search-input ${appliedFilters.length === 0 ? '-placeholder' : '-has-filters'}`}
                    />
                    {appliedFilters.length > 0 && (
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                this.handleClearAll();
                            }}
                            className="dimensions-filters__clear-link"
                            title={t('filters.clear', 'Borrar')}
                        >
                            <FaIcon iconName="times-circle" />
                        </span>
                    )}
                </div>

                {isPopupOpen && (
                    <div
                        ref={this.popupRef}
                        className={`dimensions-filters__popup ${selectedField ? '-editor' : ''}`}
                    >
                        {!selectedField ? (
                            // Mostrar grupos de métricas y dimensiones
                            <div>
                                {Object.entries(fieldsByGroup).map(([groupId, groupFields]) => {
                                    if (!groupFields || groupFields.length === 0) return null;
                                    const groupData = fieldsData.FIELDS_GROUP_DATA[groupId];
                                    return (
                                        <div key={groupId} className="dimensions-filters__group">
                                            <div className="dimensions-filters__group-title">
                                                {groupData.name}
                                            </div>
                                            <div className="dimensions-filters__group-fields">
                                                {groupFields.map(field => (
                                                    <div
                                                        key={field.id}
                                                        onClick={() => this.handleFieldSelect(field.id)}
                                                        className="dimensions-filters__field-item"
                                                    >
                                                        {field.name}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            // Mostrar configuración de filtro
                            <div className="dimensions-filters__filter-config">
                                <div className="dimensions-filters__filter-label">
                                    <div className="dimensions-filters__filter-label-text">
                                        {t('filters.filter_by', 'Filtrar por')}
                                    </div>
                                    <div className="dimensions-filters__filter-label-name">
                                        {fieldsData.FIELD_DATA_BY_ID[selectedField]?.name || selectedField}
                                    </div>
                                    {selectedFieldHint && (
                                        <div className="dimensions-filters__filter-hint" data-testid="dimensions-filters-hint">
                                            {selectedFieldHint}
                                        </div>
                                    )}
                                </div>

                                {showOptionsMenu ? (
                                    // Menú de opciones predefinidas
                                    <div>
                                        <div className="dimensions-filters__options-title">
                                            {t('filters.select_options', 'Seleccione opciones')}
                                        </div>
                                        <ul className="multi-select-list">
                                            {this.getFieldOptions(selectedField).map((option, index) => {
                                                // Manejar diferentes estructuras de opciones
                                                let optionValue, optionText;
                                                
                                                if (typeof option === 'string') {
                                                    // Si la opción es un string simple
                                                    optionValue = option;
                                                    optionText = option;
                                                } else if (typeof option === 'object' && option !== null) {
                                                    // Si es un objeto, intentar obtener value y text de diferentes propiedades
                                                    optionValue = option.value !== undefined ? option.value : option.id || option.key || index;
                                                    optionText = option.text || option.label || option.name || option.value || String(optionValue);
                                                } else {
                                                    // Fallback
                                                    optionValue = option !== undefined ? option : index;
                                                    optionText = String(optionValue);
                                                }
                                                
                                                // Traducir el texto si es BusinessUnitStatus
                                                const displayText = this.translateOptionText(selectedField, optionText);
                                                
                                                return (
                                                    <li key={optionValue}>
                                                        <label className="multi-select-item">
                                                            <input
                                                                type="checkbox"
                                                                checked={filterValues.includes(optionValue)}
                                                                onChange={() => this.handleOptionToggle(optionValue)}
                                                            />
                                                            <p className="multi-select-item-name">{displayText}</p>
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        <div className="dimensions-filters__buttons-container">
                                            <button
                                                onClick={() => {
                                                    this.setState({
                                                        selectedField: null,
                                                        filterOperator: null,
                                                        filterValues: [],
                                                        tempFilterValue: '',
                                                        tempFilterValue2: '',
                                                        showOperatorMenu: false,
                                                        showOptionsMenu: false,
                                                    });
                                                }}
                                                className="dimensions-filters__button -cancel"
                                            >
                                                {t('filters.cancel', 'Cancelar')}
                                            </button>
                                            <button
                                                onClick={this.handleApplyFilter}
                                                className="dimensions-filters__button -apply"
                                            >
                                                {t('filters.apply', 'Aplicar')}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    // Menú de operadores y valores
                                    <div>
                                        <div className="dimensions-filters__operator-container">
                                            <div
                                                onClick={() => this.setState({ showOperatorMenu: !showOperatorMenu })}
                                                className="dimensions-filters__operator-selector"
                                            >
                                                <span>
                                                    {this.getAvailableOperators(selectedField).find(op => op.value === filterOperator)?.label || filterOperator}
                                                </span>
                                                <FaIcon iconName="chevron-down" />
                                            </div>
                                            {showOperatorMenu && (
                                                <div className="dimensions-filters__operator-menu">
                                                    {this.getAvailableOperators(selectedField).map(op => (
                                                        <div
                                                            key={op.value}
                                                            onClick={() => this.handleOperatorSelect(op.value)}
                                                            className={`dimensions-filters__operator-item ${filterOperator === op.value ? '-selected' : ''}`}
                                                        >
                                                            {op.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {filterOperator && filterOperator !== 'IS_NULL' && (
                                            <div className="dimensions-filters__value-input-container">
                                                {filterOperator === 'BETWEEN' ? (
                                                    <div className="dimensions-filters__between-inputs">
                                                        <input
                                                            type="text"
                                                            value={tempFilterValue}
                                                            onChange={(e) => this.setState({ tempFilterValue: e.target.value })}
                                                            placeholder={t('filters.from', 'Desde')}
                                                            className="dimensions-filters__value-input"
                                                        />
                                                        <span>{t('filters.and', 'y')}</span>
                                                        <input
                                                            type="text"
                                                            value={tempFilterValue2}
                                                            onChange={(e) => this.setState({ tempFilterValue2: e.target.value })}
                                                            placeholder={t('filters.to', 'Hasta')}
                                                            className="dimensions-filters__value-input"
                                                        />
                                                    </div>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={tempFilterValue}
                                                        onChange={(e) => this.setState({ tempFilterValue: e.target.value })}
                                                        placeholder={t('filters.enter_value', 'Ingrese un valor')}
                                                        className="dimensions-filters__value-input -full-width"
                                                    />
                                                )}
                                            </div>
                                        )}

                                        <div className="dimensions-filters__buttons-container">
                                            <button
                                                onClick={() => {
                                                    this.setState({
                                                        selectedField: null,
                                                        filterOperator: null,
                                                        filterValues: [],
                                                        tempFilterValue: '',
                                                        tempFilterValue2: '',
                                                        showOperatorMenu: false,
                                                        showOptionsMenu: false,
                                                    });
                                                }}
                                                className="dimensions-filters__button -cancel"
                                            >
                                                {t('filters.cancel', 'Cancelar')}
                                            </button>
                                            <button
                                                onClick={this.handleApplyFilter}
                                                className="dimensions-filters__button -apply"
                                            >
                                                {t('filters.apply', 'Aplicar')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export const DimensionsFiltersComponent = withTranslation()(DimensionsFiltersComponentClass);

